import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useTranslation } from 'react-i18next';

import { clsx } from '~/styles/utils';

import { Button } from '../Button/Button';
import { useTableContext } from './TableRoot.context';
import { containerStyle, currentSelectionStyle, tableSelectionStyle, tableSelectionWrapperStyle, wrapperStyle } from './TableSelection.css';

type Props = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
  /** Actions on the selected rows, rendered with their ids. */
  children?: (selectedRowIds: number[]) => ReactNode;
};

export const TableSelection = ({ children, ...delegated }: Props) => {
  const { resetRowSelection, state } = useTableContext();
  const selectedRowIds = Object.keys(state.rowSelection).map(Number);

  const [ref] = useAutoAnimate();
  const { t } = useTranslation(['common']);

  return (
    <div {...delegated} className={clsx(tableSelectionWrapperStyle, delegated.className)} ref={ref}>
      {selectedRowIds.length > 0 && (
        <div className={tableSelectionStyle}>
          <div className={wrapperStyle}>
            <Button aria-label={t('common:datatable.selection.removeSelection')} icon="CloseSmall" onClick={() => resetRowSelection()} />

            <span className={currentSelectionStyle}>
              {t('common:datatable.selection.amountSelected', { amount: selectedRowIds.length })}
            </span>
          </div>

          {children && <div className={containerStyle}>{children(selectedRowIds)}</div>}
        </div>
      )}
    </div>
  );
};
